import React, { useEffect, useState } from "react";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { deleteDirector, fetchDirectors } from "@services/directorService";
import { filterByFullName } from "@util/filter";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { useAuth } from "@context/auth-context";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import AvailableWithPlatformAccessButton from "@components/buttons/AvailableWithPlatformAccessButton/AvailableWithPlatformAccessButton";

const RESULTS_PER_PAGE = 8;

const DirectorsPage: React.FC = () => {
    const navigate = useNavigate();
    const { isSubscribed } = useAuth();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [directors, setDirectors] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [selectedDirectorId, setSelectedDirectorId] = useState("");

    // Fetch directors on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchDirectors();
                setDirectors(data);
            } catch (err) {
                setError("Failed to fetch directors.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    // Filter and paginate directors
    const filteredData = filterByFullName(directors, searchTerm);
    const totalPages = Math.ceil(filteredData.length / RESULTS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    const handleDelete = async () => {
        try {
            setLoading(true);
            if (!selectedDirectorId) {
                throw new Error("No service ID selected.");
            }
            await deleteDirector(selectedDirectorId);
            // Update charity status locally
            setDirectors((prevState) => (
                prevState.filter((val) => val.id !== selectedDirectorId)
            ));
        } catch {
            setError("Failed to delete item.");
        } finally {
            setLoading(false);
        }
        setShowDeletePopup(false);
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!isSubscribed) {
            return (
                <div className={commonStyles.container}>
                    <AvailableWithPlatformAccessButton />
                </div>
            );
        }

        if (paginatedData.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No users ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData.map((data) => (
                    <InfoCard
                        key={data.id}
                        title={`${data.firstName} ${data.lastName}`}
                        // items={[data.businessName]}
                        buttons={[
                            {
                                title: "Edit",
                                variant: "secondary",
                                to: `editor?${new URLSearchParams({
                                    mode: "edit",
                                    id: data.id,
                                }).toString()}`,
                            },
                            {
                                title: <DeleteOutlineIcon fontSize="small" />,
                                variant: "cancel",
                                onClick: () => {
                                    setShowDeletePopup(true);
                                    setSelectedDirectorId(data.id);
                                },
                            },
                        ]}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                <h1>Company Users</h1>
                {!loading && !error && isSubscribed && (
                    <div className={commonStyles.buttonGroup}>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={() => {
                                navigate("editor?mode=add");
                            }}
                        >
                            Add
                        </Button>
                    </div>
                )}
            </div>
            {!loading && isSubscribed && (
                <div className={commonStyles.searchBar}>
                    <SearchBar
                        value={searchTerm}
                        onChange={(val) => setSearchTerm(val)}
                    />
                </div>
            )}
            {renderContent()}
            {totalPages > 1 && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
            <DeleteConfirmationPopup
                visible={showDeletePopup}
                onConfirm={handleDelete}
                onCancel={() => setShowDeletePopup(false)}
            />
        </div>
    );
};

export default DirectorsPage;
